import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useUpdateChartLayoutCallbacks } from 'context/dashboard-context';

const convertToPrecent = (data, total, precentToNumberDict) => {
  data = Object.entries(data).map(([key, value]) => {
    const precent = Math.round((value / total) * 100);
    precentToNumberDict[precent] = value;
    return precent;
  });
  return data;
};

const getOptions = (chartElement, title, subtitle, colors, labels, precentToNumberDict, total) => {
  const CHART_ELEMENT_RESIZE_THRESHOLD = 400;
  const shouldLegendBePositionRight = chartElement.clientWidth > CHART_ELEMENT_RESIZE_THRESHOLD;
  return {
    title: {
      text: title,
      floating: true,
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#333'
      }
    },
    subtitle: {
      text: subtitle,
      align: 'left',
      offsetY: 20,
      style: {
        fontSize: '10px',
        color: '#000'
      }
    },
    chart: {
      id: chartElement.id,
      type: 'radialBar',
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true
        }
      }
    },
    labels,
    colors: colors,
    legend: {
      position: shouldLegendBePositionRight ? 'right' : 'bottom',
      show: true,
      fontSize: '9px',
      markers: {
        radius: 2
      }
    },
    plotOptions: {
      radialBar: {
        offsetX: shouldLegendBePositionRight ? -40 : 0,
        offsetY: shouldLegendBePositionRight ? 30 : 20,
        hollow: {
          size: '40%'
        },
        track: {
          show: true,
          opacity: 1,
          background: '#f2f2f2'
        },
        dataLabels: {
          name: {
            show: true,
            offsetY: 20,
            fontSize: '10px'
          },
          value: {
            fontSize: '1rem',
            fontWeight: 600,
            show: true,
            offsetY: -15,
            formatter: (w) => precentToNumberDict[w]
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '0.75rem',
            offsetY: -7.5,
            color: '#000',
            formatter: () => total
          }
        }
      }
    },
    responsive: [
      {
        breakpoint: 1475,
        options: {
          title: {
            style: {
              fontSize: '14px'
            }
          }
        }
      },
      {
        breakpoint: 1300,
        options: {
          title: {
            style: {
              fontSize: '13px'
            }
          }
        }
      }
    ]
  };
};

export function RadialBarChart({ data, title, subtitle, colors }) {
  const [total, setTotal] = useState();
  const [series, setSeries] = useState();
  const [options, setOptions] = useState();

  const chartRef = useRef(null);

  const {
    updateChartLayoutCallbacks,
    addUpdateChartLayoutCallback,
    removeUpdateChartLayoutCallback
  } = useUpdateChartLayoutCallbacks();

  useEffect(() => {
    const updateChartLayout = () =>
      setOptions(getOptions(chartRef.current, title, subtitle, colors, Object.keys(data), precentToNumberDict, total));

    addUpdateChartLayoutCallback(updateChartLayout, updateChartLayoutCallbacks);
    return () => removeUpdateChartLayoutCallback(updateChartLayout, updateChartLayoutCallbacks);
  }, []);

  const precentToNumberDict = {};

  useEffect(() => {
    if (data) {
      setTotal(Object.values(data).reduce((a, b) => a + b));
    }
  }, [data]);

  useEffect(() => {
    if (total) {
      setSeries(convertToPrecent(data, total, precentToNumberDict));
      const labels = Object.keys(data);
      setOptions(getOptions(chartRef.current, title, subtitle, colors, labels, precentToNumberDict, total));
    }
  }, [total]);

  return (
    <div id={`radialbarChart-${title}`} ref={chartRef}>
      {options && series && <ReactApexChart options={options} series={series} type="radialBar" height={290} />}
    </div>
  );
}
